import { approximateUsd, formatMoney, type Money } from '@tive/core';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../lib/utils';

/**
 * A salary figure, covered up until somebody asks for it.
 *
 * The same idea Rippling uses, and for the same reason: this app is opened on a
 * shared screen and in meetings, and a number that is on the page whether or not
 * anyone meant to show it will eventually be shown to the wrong room. Hiding it
 * by default makes revealing it a decision.
 *
 * It is a cover rather than a secret — the figure is in the page either way, and
 * anybody who can open the scenario can read it. What this stops is the accident.
 */
export function SecretAmount({ cost, className }: { cost: Money; className?: string }) {
  const [shown, setShown] = useState(false);

  const usd = approximateUsd(cost);
  const isUsd = cost.currency.toUpperCase() === 'USD';
  /*
   * Only for a currency that is not dollars, and only once it is showing: the
   * conversion is the second question, and putting it on a covered figure would
   * defeat the covering.
   */
  const tooltip =
    shown && !isUsd && usd !== null
      ? `≈ ${formatMoney({ amount: usd, currency: 'USD' })} (approximate)`
      : undefined;

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {shown ? (
        <span
          title={tooltip}
          className={cn(
            'text-[12px] font-semibold text-[var(--color-ink)] tabular-nums',
            tooltip && 'decoration-dotted underline-offset-2 hover:underline',
          )}
        >
          {formatMoney(cost)}
        </span>
      ) : (
        // Ten dots whatever the figure is: a mask that got longer for a bigger
        // number would leak the thing it is covering.
        <span aria-hidden className="text-[12px] leading-none tracking-tight text-[var(--color-ink)]">
          ●●●●●●●●●●
        </span>
      )}
      <button
        type="button"
        onClick={() => setShown((value) => !value)}
        aria-label={shown ? 'Hide the amount' : 'Show the amount'}
        className="text-[var(--color-ink-3)] transition-colors hover:text-[var(--color-ink)]"
      >
        {shown ? <EyeOff size={13} /> : <Eye size={13} />}
      </button>
    </span>
  );
}
